import { z } from 'zod';

import {
  CashDenominationConfidenceSchema,
  CashDenominationRowsSchema,
  CashDenominationSourceSchema,
  CashFundingSourceTypeSchema,
  CashModeActivationCountDtoSchema,
  CashMovementDirectionSchema,
  CashMovementDtoSchema,
  CashMovementKindSchema,
  CashMovementSourceTypeSchema,
  CashVarianceActionDtoSchema,
  CashVarianceCaseDtoSchema,
} from '../cash/index.js';
import {
  CASH_VARIANCE_OPERATION_ENTITY_TYPES,
  cashTargetStateSyncEntityTypeValues,
  cashTargetStateSyncEventTypeValues,
  cashVarianceOperationSyncEventTypeValues,
  type CashTargetStateSyncEntityType,
  type CashTargetStateSyncEventType,
  type CashTargetStateSyncMutationIdentityDto,
  type CashVarianceOperationSyncDataDto,
  type CashVarianceOperationSyncEventType,
} from './cash-target-state-sync.dto.js';
import type { CashSyncOperationEnvelopeDto } from './cash-sync.dto.js';

export const CashTargetStateSyncEntityTypeSchema: z.ZodType<CashTargetStateSyncEntityType> = z.enum(
  cashTargetStateSyncEntityTypeValues,
);

export const CashTargetStateSyncEventTypeSchema: z.ZodType<CashTargetStateSyncEventType> = z.enum(
  cashTargetStateSyncEventTypeValues,
);

const CashTargetStateSyncMutationIdentityObjectSchema = z.object({
  operationId: z.uuid(),
  requestHash: z.string().trim().min(32).max(128),
  companyId: z.uuid(),
  scopeType: z.enum(['location', 'treasury']),
  scopeId: z.uuid(),
  actorUserId: z.uuid(),
  occurredAt: z.iso.datetime(),
});

export const CashTargetStateSyncMutationIdentityDtoSchema: z.ZodType<CashTargetStateSyncMutationIdentityDto> =
  CashTargetStateSyncMutationIdentityObjectSchema;

const CashVarianceOperationEnvelopeDtoSchema: z.ZodType<CashSyncOperationEnvelopeDto> = z
  .object({
    operationId: z.uuid(),
    requestHash: z.string().regex(/^[a-f0-9]{64}$/u),
    companyId: z.uuid(),
    scopeType: z.enum(['location', 'treasury']),
    scopeId: z.uuid(),
    actorUserId: z.uuid(),
    resultEntityType: z.string().trim().min(1).max(100),
    resultEntityId: z.uuid(),
    occurredAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
  })
  .strict();

const uuidSchema = z.uuid();
const nullableUuidSchema = uuidSchema.nullable();
const instantSchema = z.iso.datetime();
const nullableInstantSchema = instantSchema.nullable();
const nullableStringSchema = z.string().nullable();
const requestHashSchema = z.string().regex(/^[a-f0-9]{64}$/u);
const denominationRowsJsonSchema = CashDenominationRowsSchema.nullable();

const CashScopeModeStateSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    scopeType: z.enum(['location', 'treasury']),
    scopeId: uuidSchema,
    effectiveMode: z.enum(['simple', 'denomination_inventory']),
    transition: z.enum([
      'none',
      'pending_activation',
      'ready_for_cutover',
      'pending_simple_transition',
    ]),
    configurationRevision: z.number().int().positive(),
    capturedEntitlementRevision: z.number().int().nonnegative(),
    activeActivationId: nullableUuidSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashModeActivationSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    scopeType: z.enum(['location', 'treasury']),
    scopeId: uuidSchema,
    targetMode: z.enum(['simple', 'denomination_inventory']),
    effectiveMode: z.enum(['simple', 'denomination_inventory']),
    transition: z.enum([
      'none',
      'pending_activation',
      'ready_for_cutover',
      'pending_simple_transition',
    ]),
    status: z.enum(['pending_activation', 'ready_for_cutover', 'completed', 'cancelled']),
    configurationRevision: z.number().int().positive(),
    capturedEntitlementRevision: z.number().int().nonnegative(),
    countsJson: z.array(CashModeActivationCountDtoSchema),
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    activatedByUserId: nullableUuidSchema,
    activatedAt: nullableInstantSchema,
    cancelledByUserId: nullableUuidSchema,
    cancelledAt: nullableInstantSchema,
    cancellationReason: nullableStringSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashNeededRequestSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    storeId: uuidSchema,
    locationId: uuidSchema,
    stationId: uuidSchema,
    cashSessionId: uuidSchema,
    drawerCashLocationId: uuidSchema,
    requestedAmountMinor: z.number().int().positive(),
    note: nullableStringSchema,
    status: z.enum(['open', 'fulfilled', 'cancelled']),
    createdByUserId: uuidSchema,
    fulfilledByCashTransferId: nullableUuidSchema,
    fulfilledAt: nullableInstantSchema,
    fulfilledByUserId: nullableUuidSchema,
    cancelledAt: nullableInstantSchema,
    cancelledByUserId: nullableUuidSchema,
    cancellationReason: nullableStringSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashLocalTransferSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    storeId: uuidSchema,
    locationId: uuidSchema,
    stationId: uuidSchema,
    kind: z.enum(['drawer_refill', 'drawer_drop', 'drawer_to_drawer']),
    status: z.enum(['applied', 'reversed']),
    sourceCashLocationId: uuidSchema,
    sourceCashSessionId: nullableUuidSchema,
    destinationCashLocationId: uuidSchema,
    destinationCashSessionId: nullableUuidSchema,
    amountMinor: z.number().int().positive(),
    note: nullableStringSchema,
    denominationsJson: denominationRowsJsonSchema,
    sourceMovementId: uuidSchema,
    destinationMovementId: uuidSchema,
    cashNeededRequestId: nullableUuidSchema,
    reversedByCashTransferId: nullableUuidSchema,
    reversesCashTransferId: nullableUuidSchema,
    replacesCashTransferId: nullableUuidSchema,
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashTransferSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    storeId: nullableUuidSchema,
    locationId: nullableUuidSchema,
    stationId: nullableUuidSchema,
    fromCashLocationId: uuidSchema,
    toCashLocationId: uuidSchema,
    amount: z.number().int().positive(),
    type: z.enum(['transfer', 'drop']),
    notes: nullableStringSchema,
    createdByUserId: uuidSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashMovementSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    storeId: nullableUuidSchema,
    locationId: nullableUuidSchema,
    stationId: nullableUuidSchema,
    cashLocationId: uuidSchema,
    cashSessionId: nullableUuidSchema,
    transferId: nullableUuidSchema,
    expenseId: nullableUuidSchema,
    movementKind: CashMovementKindSchema,
    direction: CashMovementDirectionSchema,
    amount: z.number().int().positive(),
    description: nullableStringSchema,
    isReversal: z.boolean(),
    reversalOfMovementId: nullableUuidSchema,
    sourceType: CashMovementSourceTypeSchema.nullable(),
    sourceId: nullableUuidSchema,
    fundingSourceCategoryId: nullableUuidSchema,
    fundingSourceCategoryCodeSnapshot: nullableStringSchema,
    fundingSourceCategoryDisplayNameSnapshot: nullableStringSchema,
    fundingSourceType: CashFundingSourceTypeSchema.nullable(),
    fundingReference: nullableStringSchema,
    reasonCode: nullableStringSchema,
    varianceCaseId: nullableUuidSchema,
    varianceActionId: nullableUuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashLocalTransferCorrectionSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    storeId: uuidSchema,
    locationId: uuidSchema,
    stationId: uuidSchema,
    originalCashTransferId: uuidSchema,
    actualAmountMinor: z.number().int().nonnegative(),
    reason: z.string(),
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashExpenseMetadataCorrectionSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    expenseId: uuidSchema,
    priorDescription: z.string(),
    priorCategoryId: nullableUuidSchema,
    currentDescription: z.string(),
    currentCategoryId: nullableUuidSchema,
    reason: z.string(),
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
  })
  .strict();

const CashExpenseSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    sourceType: z.enum(['drawer', 'vault', 'treasury']),
    storeId: nullableUuidSchema,
    locationId: nullableUuidSchema,
    stationId: nullableUuidSchema,
    cashLocationId: uuidSchema,
    cashSessionId: nullableUuidSchema,
    categoryId: nullableUuidSchema,
    operationId: nullableUuidSchema,
    requestHash: requestHashSchema.nullable(),
    replacesExpenseId: nullableUuidSchema,
    amount: z.number().int().positive(),
    description: z.string(),
    status: z.enum(['active', 'voided']),
    createdByUserId: uuidSchema,
    voidedByUserId: nullableUuidSchema,
    voidReason: nullableStringSchema,
    occurredAt: instantSchema,
    voidedAt: nullableInstantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashDenominationCorrectionSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    cashMovementId: uuidSchema,
    cashLocationId: uuidSchema,
    cashSessionId: nullableUuidSchema,
    allocationId: uuidSchema,
    priorAllocationRevisionId: uuidSchema,
    correctedAllocationRevisionId: uuidSchema,
    priorRowsJson: CashDenominationRowsSchema,
    correctedRowsJson: CashDenominationRowsSchema,
    effect: z.enum(['current_inventory', 'audit_only']),
    countBoundaryCheckpointId: nullableUuidSchema,
    reason: z.string(),
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
  })
  .strict();

const CashDenominationAllocationSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    storeId: nullableUuidSchema,
    locationId: nullableUuidSchema,
    stationId: nullableUuidSchema,
    cashMovementId: uuidSchema,
    cashLocationId: uuidSchema,
    cashSessionId: nullableUuidSchema,
    currentRevisionId: nullableUuidSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashDenominationRevisionRowSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    revisionId: uuidSchema,
    denominationMinor: z.number().int().positive(),
    count: z.number().int().positive(),
    rowTotalMinor: z.number().int().positive(),
    createdAt: instantSchema,
  })
  .strict();

const CashDenominationRevisionSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    allocationId: uuidSchema,
    cashMovementId: uuidSchema,
    source: CashDenominationSourceSchema,
    confidence: CashDenominationConfidenceSchema,
    denominationTotalMinor: z.number().int().nonnegative(),
    effectiveAt: instantSchema,
    note: nullableStringSchema,
    createdByUserId: nullableUuidSchema,
    createdAt: instantSchema,
    rows: z.array(CashDenominationRevisionRowSnapshotSchema),
  })
  .strict();

const CashTreasuryDistributionRequestSnapshotSchema = z
  .object({
    id: uuidSchema,
    companyId: uuidSchema,
    idempotencyKey: z.string(),
    treasuryCashLocationId: uuidSchema,
    targetCashLocationId: uuidSchema,
    targetStoreId: uuidSchema,
    targetLocationId: uuidSchema,
    targetStationId: uuidSchema,
    amountMinor: z.number().int().positive(),
    denominationsJson: denominationRowsJsonSchema,
    status: z.enum(['requested', 'accepted', 'declined', 'cancelled', 'expired']),
    requestedByUserId: uuidSchema,
    acceptedByUserId: nullableUuidSchema,
    declinedByUserId: nullableUuidSchema,
    cancelledByUserId: nullableUuidSchema,
    requestedAt: instantSchema,
    acceptedAt: nullableInstantSchema,
    declinedAt: nullableInstantSchema,
    cancelledAt: nullableInstantSchema,
    expiredAt: nullableInstantSchema,
    expiresAt: nullableInstantSchema,
    requestNote: nullableStringSchema,
    declineReason: nullableStringSchema,
    cancellationReason: nullableStringSchema,
    ledgerAppliedAt: nullableInstantSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashCustodyReceiptSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    status: z.enum([
      'accepted',
      'matched_pending_ack',
      'pending_reconciliation',
      'matched',
      'reclassified',
      'corrected',
      'exception',
      'reversed',
    ]),
    sourceType: z.enum([
      'treasury_instruction',
      'direct_vault_intake',
      'unclassified_physical_source',
      'custody_receipt_reversal',
    ]),
    sourceReference: z.string(),
    storeId: uuidSchema,
    locationId: uuidSchema,
    stationId: uuidSchema,
    targetVaultCashLocationId: uuidSchema,
    amountMinor: z.number().int().positive(),
    denominationsJson: denominationRowsJsonSchema,
    evidenceNote: nullableStringSchema,
    treasuryInstructionId: nullableUuidSchema,
    originalReceiptId: nullableUuidSchema,
    vaultInMovementId: nullableUuidSchema,
    vaultOutMovementId: nullableUuidSchema,
    treasuryOutMovementId: nullableUuidSchema,
    treasuryInMovementId: nullableUuidSchema,
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    reconciledByUserId: nullableUuidSchema,
    reconciledAt: nullableInstantSchema,
    exceptionReason: nullableStringSchema,
    reversedByReceiptId: nullableUuidSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashCustodyReceiptReconciliationSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    receiptId: uuidSchema,
    priorStatus: CashCustodyReceiptSnapshotSchema.shape.status,
    resultingStatus: CashCustodyReceiptSnapshotSchema.shape.status,
    resultingSourceType: CashCustodyReceiptSnapshotSchema.shape.sourceType,
    treasuryInstructionId: nullableUuidSchema,
    treasuryOutMovementId: nullableUuidSchema,
    reason: nullableStringSchema,
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
  })
  .strict();

const CashVaultRemittanceSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    storeId: uuidSchema,
    locationId: uuidSchema,
    stationId: uuidSchema,
    sourceVaultCashLocationId: uuidSchema,
    destinationType: z.enum(['central_treasury', 'bank_deposit', 'external_physical']),
    destinationId: uuidSchema,
    destinationCashLocationIdSnapshot: nullableUuidSchema,
    status: z.enum([
      'draft',
      'prepared',
      'awaiting_destination_confirmation',
      'confirmed',
      'cancelled',
      'returned',
      'exception',
      'voided',
    ]),
    amountMinor: z.number().int().positive(),
    denominationsJson: denominationRowsJsonSchema,
    reference: nullableStringSchema,
    note: nullableStringSchema,
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    preparedByUserId: nullableUuidSchema,
    preparedAt: nullableInstantSchema,
    handedOffByUserId: nullableUuidSchema,
    handedOffAt: nullableInstantSchema,
    vaultOutMovementId: nullableUuidSchema,
    destinationMovementId: nullableUuidSchema,
    returnedVaultInMovementId: nullableUuidSchema,
    resolutionReason: nullableStringSchema,
    createdAt: instantSchema,
    updatedAt: instantSchema,
  })
  .strict();

const CashVaultRemittanceEventSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    remittanceId: uuidSchema,
    eventType: z.enum([
      'created',
      'updated',
      'prepared',
      'handed_off',
      'confirmed',
      'cancelled',
      'returned',
      'excepted',
      'voided',
    ]),
    priorStatus: CashVaultRemittanceSnapshotSchema.shape.status.nullable(),
    resultingStatus: CashVaultRemittanceSnapshotSchema.shape.status,
    evidenceReference: nullableStringSchema,
    actualAmountMinor: z.number().int().positive().nullable(),
    actualDenominationsJson: denominationRowsJsonSchema,
    reason: nullableStringSchema,
    actorUserId: uuidSchema,
    occurredAt: instantSchema,
    createdAt: instantSchema,
  })
  .strict();

const CashAvailabilityReservationSnapshotSchema = z
  .object({
    id: uuidSchema,
    operationId: uuidSchema,
    requestHash: requestHashSchema,
    companyId: uuidSchema,
    cashLocationId: uuidSchema,
    remittanceId: uuidSchema,
    amountMinor: z.number().int().positive(),
    denominationsJson: denominationRowsJsonSchema,
    status: z.enum(['active', 'released', 'consumed']),
    createdByUserId: uuidSchema,
    createdAt: instantSchema,
    releasedByOperationId: nullableUuidSchema,
    releasedAt: nullableInstantSchema,
    consumedByOperationId: nullableUuidSchema,
    consumedAt: nullableInstantSchema,
  })
  .strict();

export const CashVarianceOperationSyncDataDtoSchema: z.ZodType<CashVarianceOperationSyncDataDto> = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    cases: z.array(CashVarianceCaseDtoSchema),
    actions: z.array(CashVarianceActionDtoSchema),
    movements: z.array(CashMovementDtoSchema),
  })
  .strict();

const CashModeActivationSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    activation: CashModeActivationSnapshotSchema,
    state: CashScopeModeStateSnapshotSchema,
  })
  .strict();

const CashScopeModeStateSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    state: CashScopeModeStateSnapshotSchema,
  })
  .strict();

const CashNeededRequestSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    request: CashNeededRequestSnapshotSchema,
  })
  .strict();

const CashLocalTransferSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    transfer: CashLocalTransferSnapshotSchema,
    reversedTransfer: CashLocalTransferSnapshotSchema.nullable(),
    replacedTransfer: CashLocalTransferSnapshotSchema.nullable(),
    linkedRequest: CashNeededRequestSnapshotSchema.nullable(),
    cashTransfer: CashTransferSnapshotSchema,
    sourceMovement: CashMovementSnapshotSchema,
    destinationMovement: CashMovementSnapshotSchema,
  })
  .strict();

const CashLocalTransferCorrectionSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    correction: CashLocalTransferCorrectionSnapshotSchema,
    originalTransfer: CashLocalTransferSnapshotSchema,
  })
  .strict();

const CashExpenseMetadataCorrectionSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    correction: CashExpenseMetadataCorrectionSnapshotSchema,
    expense: CashExpenseSnapshotSchema,
  })
  .strict();

const CashExpenseReplacementSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    replacement: CashExpenseSnapshotSchema,
    originalExpense: CashExpenseSnapshotSchema,
    originalMovement: CashMovementSnapshotSchema,
    reversalMovement: CashMovementSnapshotSchema,
    replacementMovement: CashMovementSnapshotSchema,
  })
  .strict();

const CashDenominationCorrectionSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    correction: CashDenominationCorrectionSnapshotSchema,
    movement: CashMovementSnapshotSchema,
    allocation: CashDenominationAllocationSnapshotSchema,
    priorRevision: CashDenominationRevisionSnapshotSchema,
    correctedRevision: CashDenominationRevisionSnapshotSchema,
  })
  .strict();

const CashTreasuryDistributionRequestSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    instruction: CashTreasuryDistributionRequestSnapshotSchema,
  })
  .strict();

const CashCustodyReceiptRecordedSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    receipt: CashCustodyReceiptSnapshotSchema,
    reconciliation: z.null(),
    vaultInMovement: CashMovementSnapshotSchema,
    treasuryOutMovement: CashMovementSnapshotSchema.nullable(),
  })
  .strict();

const CashCustodyReceiptReconciledSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    receipt: CashCustodyReceiptSnapshotSchema,
    reconciliation: CashCustodyReceiptReconciliationSnapshotSchema,
    vaultInMovement: CashMovementSnapshotSchema,
    treasuryOutMovement: CashMovementSnapshotSchema.nullable(),
  })
  .strict();

const CashCustodyReceiptReversedSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    receipt: CashCustodyReceiptSnapshotSchema,
    originalReceipt: CashCustodyReceiptSnapshotSchema,
    reconciliation: z.null(),
    vaultInMovement: z.null(),
    vaultOutMovement: CashMovementSnapshotSchema,
    treasuryOutMovement: z.null(),
    treasuryInMovement: CashMovementSnapshotSchema.nullable(),
  })
  .strict();

const CashVaultRemittanceSyncDataSchema = z
  .object({
    operation: CashVarianceOperationEnvelopeDtoSchema,
    remittance: CashVaultRemittanceSnapshotSchema,
    event: CashVaultRemittanceEventSnapshotSchema,
    reservation: CashAvailabilityReservationSnapshotSchema.nullable(),
    vaultOutMovement: CashMovementSnapshotSchema.nullable(),
    returnedVaultInMovement: CashMovementSnapshotSchema.nullable(),
  })
  .strict();

type NonVarianceTargetStateEventType = Exclude<
  CashTargetStateSyncEventType,
  CashVarianceOperationSyncEventType
>;

const nonVarianceDataSchemaByEvent: Record<NonVarianceTargetStateEventType, z.ZodType> = {
  cashModeActivationStarted: CashModeActivationSyncDataSchema,
  cashModeActivationReady: CashModeActivationSyncDataSchema,
  cashModeActivationCompleted: CashModeActivationSyncDataSchema,
  cashModeActivationCancelled: CashModeActivationSyncDataSchema,
  cashScopeModeStateChanged: CashScopeModeStateSyncDataSchema,
  cashNeededRequestOpened: CashNeededRequestSyncDataSchema,
  cashNeededRequestFulfilled: CashNeededRequestSyncDataSchema,
  cashNeededRequestCancelled: CashNeededRequestSyncDataSchema,
  cashLocalTransferApplied: CashLocalTransferSyncDataSchema,
  cashLocalTransferReversed: CashLocalTransferSyncDataSchema,
  cashLocalTransferReplaced: CashLocalTransferSyncDataSchema,
  cashLocalTransferCorrected: CashLocalTransferCorrectionSyncDataSchema,
  cashExpenseMetadataCorrected: CashExpenseMetadataCorrectionSyncDataSchema,
  cashExpenseReplaced: CashExpenseReplacementSyncDataSchema,
  cashDenominationsCorrected: CashDenominationCorrectionSyncDataSchema,
  cashTreasuryDistributionRequestDeclined: CashTreasuryDistributionRequestSyncDataSchema,
  cashCustodyReceiptRecorded: CashCustodyReceiptRecordedSyncDataSchema,
  cashCustodyReceiptReversed: CashCustodyReceiptReversedSyncDataSchema,
  cashCustodyReceiptReconciled: CashCustodyReceiptReconciledSyncDataSchema,
  cashVaultRemittanceCreated: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceUpdated: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittancePrepared: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceHandedOff: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceConfirmed: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceCancelled: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceReturned: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceExcepted: CashVaultRemittanceSyncDataSchema,
  cashVaultRemittanceVoided: CashVaultRemittanceSyncDataSchema,
};

const expectedNonVarianceResultEntityType: Record<NonVarianceTargetStateEventType, string> = {
  cashModeActivationStarted: 'cashModeActivation',
  cashModeActivationReady: 'cashModeActivation',
  cashModeActivationCompleted: 'cashModeActivation',
  cashModeActivationCancelled: 'cashModeActivation',
  cashScopeModeStateChanged: 'cashScopeModeState',
  cashNeededRequestOpened: 'cashNeededRequest',
  cashNeededRequestFulfilled: 'cashNeededRequest',
  cashNeededRequestCancelled: 'cashNeededRequest',
  cashLocalTransferApplied: 'cashLocalTransfer',
  cashLocalTransferReversed: 'cashLocalTransfer',
  cashLocalTransferReplaced: 'cashLocalTransfer',
  cashLocalTransferCorrected: 'cashLocalTransferCorrection',
  cashExpenseMetadataCorrected: 'cashExpenseMetadataCorrection',
  cashExpenseReplaced: 'cashExpenseReplacement',
  cashDenominationsCorrected: 'cashDenominationCorrection',
  cashTreasuryDistributionRequestDeclined: 'cashTreasuryDistributionRequest',
  cashCustodyReceiptRecorded: 'cashCustodyReceipt',
  cashCustodyReceiptReversed: 'cashCustodyReceipt',
  cashCustodyReceiptReconciled: 'cashCustodyReceipt',
  cashVaultRemittanceCreated: 'cashVaultRemittance',
  cashVaultRemittanceUpdated: 'cashVaultRemittance',
  cashVaultRemittancePrepared: 'cashVaultRemittance',
  cashVaultRemittanceHandedOff: 'cashVaultRemittance',
  cashVaultRemittanceConfirmed: 'cashVaultRemittance',
  cashVaultRemittanceCancelled: 'cashVaultRemittance',
  cashVaultRemittanceReturned: 'cashVaultRemittance',
  cashVaultRemittanceExcepted: 'cashVaultRemittance',
  cashVaultRemittanceVoided: 'cashVaultRemittance',
};

type TargetStatePayloadForValidation = CashTargetStateSyncMutationIdentityDto & {
  id: string;
  entityType: CashTargetStateSyncEntityType;
  eventType: CashTargetStateSyncEventType;
  resultEntityType: string;
  data: Record<string, unknown>;
};

function strictCanonicalResultGraph<TSchema extends z.ZodType>(
  schema: TSchema,
): z.ZodPipe<z.ZodUnknown, z.ZodTransform<Awaited<z.core.output<TSchema>>>> {
  return z.unknown().transform((candidate, context) => {
    const parsed = schema.safeParse(candidate);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        context.addIssue({ code: 'custom', message: issue.message, path: issue.path });
      }
      return z.NEVER;
    }
    if (!canonicalJsonEqual(candidate, parsed.data)) {
      context.addIssue({
        code: 'custom',
        message: 'Target-state result graph must already be strict and canonical.',
        path: [],
      });
      return z.NEVER;
    }
    return parsed.data;
  });
}

function targetStatePayloadArm<
  const TEventType extends CashTargetStateSyncEventType,
  const TEntityType extends CashTargetStateSyncEntityType,
  TResultEntityTypeSchema extends z.ZodType<string>,
  TDataSchema extends z.ZodType,
>(
  eventType: TEventType,
  entityType: TEntityType,
  resultEntityType: TResultEntityTypeSchema,
  data: TDataSchema,
): z.ZodObject<
  {
    operationId: z.ZodUUID;
    requestHash: z.ZodString;
    companyId: z.ZodUUID;
    scopeType: z.ZodEnum<{ location: 'location'; treasury: 'treasury' }>;
    scopeId: z.ZodUUID;
    actorUserId: z.ZodUUID;
    occurredAt: z.ZodISODateTime;
    id: z.ZodUUID;
    entityType: z.ZodLiteral<TEntityType>;
    eventType: z.ZodLiteral<TEventType>;
    resultEntityType: TResultEntityTypeSchema;
    data: z.ZodPipe<z.ZodUnknown, z.ZodTransform<Awaited<z.core.output<TDataSchema>>>>;
  },
  z.core.$strict
> {
  return CashTargetStateSyncMutationIdentityObjectSchema.extend({
    id: z.uuid(),
    entityType: z.literal(entityType),
    eventType: z.literal(eventType),
    resultEntityType,
    data: strictCanonicalResultGraph(data),
  }).strict();
}

export const CashTargetStateSyncPayloadDtoSchema = z
  .discriminatedUnion('eventType', [
    targetStatePayloadArm(
      'cashModeActivationStarted',
      'cashModeActivation',
      z.literal('cashModeActivation'),
      CashModeActivationSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashModeActivationReady',
      'cashModeActivation',
      z.literal('cashModeActivation'),
      CashModeActivationSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashModeActivationCompleted',
      'cashModeActivation',
      z.literal('cashModeActivation'),
      CashModeActivationSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashModeActivationCancelled',
      'cashModeActivation',
      z.literal('cashModeActivation'),
      CashModeActivationSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashScopeModeStateChanged',
      'cashScopeModeState',
      z.literal('cashScopeModeState'),
      CashScopeModeStateSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashNeededRequestOpened',
      'cashNeededRequest',
      z.literal('cashNeededRequest'),
      CashNeededRequestSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashNeededRequestFulfilled',
      'cashNeededRequest',
      z.literal('cashNeededRequest'),
      CashNeededRequestSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashNeededRequestCancelled',
      'cashNeededRequest',
      z.literal('cashNeededRequest'),
      CashNeededRequestSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashLocalTransferApplied',
      'cashLocalTransfer',
      z.literal('cashLocalTransfer'),
      CashLocalTransferSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashLocalTransferReversed',
      'cashLocalTransfer',
      z.literal('cashLocalTransfer'),
      CashLocalTransferSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashLocalTransferReplaced',
      'cashLocalTransfer',
      z.literal('cashLocalTransfer'),
      CashLocalTransferSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashLocalTransferCorrected',
      'cashLocalTransferCorrection',
      z.literal('cashLocalTransferCorrection'),
      CashLocalTransferCorrectionSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashExpenseMetadataCorrected',
      'cashExpenseMetadataCorrection',
      z.literal('cashExpenseMetadataCorrection'),
      CashExpenseMetadataCorrectionSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashExpenseReplaced',
      'cashExpenseMetadataCorrection',
      z.literal('cashExpenseReplacement'),
      CashExpenseReplacementSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashDenominationsCorrected',
      'cashDenominationCorrection',
      z.literal('cashDenominationCorrection'),
      CashDenominationCorrectionSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashTreasuryDistributionRequestDeclined',
      'cashTreasuryDistributionRequest',
      z.literal('cashTreasuryDistributionRequest'),
      CashTreasuryDistributionRequestSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashCustodyReceiptRecorded',
      'cashCustodyReceipt',
      z.literal('cashCustodyReceipt'),
      CashCustodyReceiptRecordedSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashCustodyReceiptReversed',
      'cashCustodyReceipt',
      z.literal('cashCustodyReceipt'),
      CashCustodyReceiptReversedSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashCustodyReceiptReconciled',
      'cashCustodyReceipt',
      z.literal('cashCustodyReceipt'),
      CashCustodyReceiptReconciledSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceCreated',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceUpdated',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittancePrepared',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceHandedOff',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceConfirmed',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceCancelled',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceReturned',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceExcepted',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVaultRemittanceVoided',
      'cashVaultRemittance',
      z.literal('cashVaultRemittance'),
      CashVaultRemittanceSyncDataSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceRecognized',
      'cashVarianceOperation',
      z.enum(['cashSessionOpen', 'cashSessionClose', 'cashSessionForceClose']),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceRecoverySubmitted',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoverySubmitted),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceRecoveryApproved',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoveryApproved),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceRecoveryRejected',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoveryRejected),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceWriteoffApplied',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.writeoffApplied),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceOffsetApplied',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.offsetApplied),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceOverageClassified',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.overageClassified),
      CashVarianceOperationSyncDataDtoSchema,
    ),
    targetStatePayloadArm(
      'cashVarianceOverageRemoved',
      'cashVarianceOperation',
      z.literal(CASH_VARIANCE_OPERATION_ENTITY_TYPES.overageRemoved),
      CashVarianceOperationSyncDataDtoSchema,
    ),
  ])
  .superRefine((value, context) => {
    const validationValue: TargetStatePayloadForValidation = value;
    if (value.entityType === 'cashVarianceOperation') {
      validateCashVarianceOperationPayload(validationValue, context);
      return;
    }
    validateNonVarianceTargetStatePayload(validationValue, context);
  });

function validateNonVarianceTargetStatePayload(
  value: TargetStatePayloadForValidation,
  context: z.RefinementCtx,
): void {
  if (isCashVarianceOperationEventType(value.eventType)) {
    return;
  }

  const eventType = value.eventType;
  const parsedData = nonVarianceDataSchemaByEvent[eventType].safeParse(value.data);
  if (!parsedData.success) {
    for (const issue of parsedData.error.issues) {
      context.addIssue({
        code: 'custom',
        message: issue.message,
        path: ['data', ...issue.path],
      });
    }
    return;
  }

  const data = parsedData.data as Record<string, unknown>;
  if (!canonicalJsonEqual(value.data, data)) {
    context.addIssue({
      code: 'custom',
      message: 'Target-state result graph must already be in canonical form.',
      path: ['data'],
    });
  }

  const operation = data.operation as CashSyncOperationEnvelopeDto;
  if (
    operation.operationId !== value.operationId ||
    operation.requestHash !== value.requestHash ||
    operation.companyId !== value.companyId ||
    operation.scopeType !== value.scopeType ||
    operation.scopeId !== value.scopeId ||
    operation.actorUserId !== value.actorUserId ||
    operation.occurredAt !== value.occurredAt ||
    operation.resultEntityType !== value.resultEntityType ||
    operation.resultEntityId !== value.id
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Target-state operation envelope must equal the top-level mutation identity.',
      path: ['data', 'operation'],
    });
  }

  if (value.resultEntityType !== expectedNonVarianceResultEntityType[eventType]) {
    context.addIssue({
      code: 'custom',
      message: 'Target-state event type does not match its canonical result entity type.',
      path: ['resultEntityType'],
    });
  }

  const primaryId = primaryResultGraphId(eventType, data);
  if (primaryId !== value.id) {
    context.addIssue({
      code: 'custom',
      message: 'Target-state result identity must equal its primary graph identity.',
      path: ['id'],
    });
  }

  validateGraphCompanyIds(data, value.companyId, context, ['data']);
  validateNonVarianceEventSemantics(eventType, data, context);
}

function primaryResultGraphId(
  eventType: NonVarianceTargetStateEventType,
  data: Record<string, unknown>,
): unknown {
  switch (eventType) {
    case 'cashModeActivationStarted':
    case 'cashModeActivationReady':
    case 'cashModeActivationCompleted':
    case 'cashModeActivationCancelled':
      return recordAt(data, 'activation').id;
    case 'cashScopeModeStateChanged':
      return recordAt(data, 'state').id;
    case 'cashNeededRequestOpened':
    case 'cashNeededRequestFulfilled':
    case 'cashNeededRequestCancelled':
      return recordAt(data, 'request').id;
    case 'cashLocalTransferApplied':
    case 'cashLocalTransferReversed':
    case 'cashLocalTransferReplaced':
      return recordAt(data, 'transfer').id;
    case 'cashLocalTransferCorrected':
    case 'cashExpenseMetadataCorrected':
    case 'cashDenominationsCorrected':
      return recordAt(data, 'correction').id;
    case 'cashExpenseReplaced':
      return recordAt(data, 'replacement').id;
    case 'cashTreasuryDistributionRequestDeclined':
      return recordAt(data, 'instruction').id;
    case 'cashCustodyReceiptRecorded':
    case 'cashCustodyReceiptReversed':
    case 'cashCustodyReceiptReconciled':
      return recordAt(data, 'receipt').id;
    case 'cashVaultRemittanceCreated':
    case 'cashVaultRemittanceUpdated':
    case 'cashVaultRemittancePrepared':
    case 'cashVaultRemittanceHandedOff':
    case 'cashVaultRemittanceConfirmed':
    case 'cashVaultRemittanceCancelled':
    case 'cashVaultRemittanceReturned':
    case 'cashVaultRemittanceExcepted':
    case 'cashVaultRemittanceVoided':
      return recordAt(data, 'remittance').id;
  }
}

function validateNonVarianceEventSemantics(
  eventType: NonVarianceTargetStateEventType,
  data: Record<string, unknown>,
  context: z.RefinementCtx,
): void {
  const modeStatusByEvent = {
    cashModeActivationStarted: 'pending_activation',
    cashModeActivationReady: 'ready_for_cutover',
    cashModeActivationCompleted: 'completed',
    cashModeActivationCancelled: 'cancelled',
  } as const;
  if (eventType in modeStatusByEvent) {
    const expectedStatus = modeStatusByEvent[eventType as keyof typeof modeStatusByEvent];
    addGraphIssueUnless(
      recordAt(data, 'activation').status === expectedStatus,
      context,
      ['data', 'activation', 'status'],
      'Cash mode activation event does not match its status.',
    );
    return;
  }

  const neededStatusByEvent = {
    cashNeededRequestOpened: 'open',
    cashNeededRequestFulfilled: 'fulfilled',
    cashNeededRequestCancelled: 'cancelled',
  } as const;
  if (eventType in neededStatusByEvent) {
    const request = recordAt(data, 'request');
    const expectedStatus = neededStatusByEvent[eventType as keyof typeof neededStatusByEvent];
    addGraphIssueUnless(
      request.status === expectedStatus,
      context,
      ['data', 'request', 'status'],
      'Cash-needed request event does not match its status.',
    );
    return;
  }

  if (
    eventType === 'cashLocalTransferApplied' ||
    eventType === 'cashLocalTransferReversed' ||
    eventType === 'cashLocalTransferReplaced'
  ) {
    const transfer = recordAt(data, 'transfer');
    const cashTransfer = recordAt(data, 'cashTransfer');
    const sourceMovement = recordAt(data, 'sourceMovement');
    const destinationMovement = recordAt(data, 'destinationMovement');
    const expectedLinkedGraph =
      eventType === 'cashLocalTransferApplied'
        ? data.reversedTransfer === null && data.replacedTransfer === null
        : eventType === 'cashLocalTransferReversed'
          ? data.reversedTransfer !== null && data.replacedTransfer === null
          : data.reversedTransfer === null && data.replacedTransfer !== null;
    addGraphIssueUnless(
      expectedLinkedGraph,
      context,
      ['data'],
      'Cash local-transfer event has the wrong linked transfer graph.',
    );
    addGraphIssueUnless(
      cashTransfer.id === transfer.id &&
        sourceMovement.id === transfer.sourceMovementId &&
        destinationMovement.id === transfer.destinationMovementId &&
        sourceMovement.transferId === transfer.id &&
        destinationMovement.transferId === transfer.id,
      context,
      ['data'],
      'Cash local-transfer graph identities do not match.',
    );
    return;
  }

  if (eventType === 'cashLocalTransferCorrected') {
    const correction = recordAt(data, 'correction');
    addGraphIssueUnless(
      recordAt(data, 'originalTransfer').id === correction.originalCashTransferId,
      context,
      ['data', 'originalTransfer', 'id'],
      'Cash local-transfer correction does not identify its original transfer.',
    );
    return;
  }

  if (eventType === 'cashExpenseMetadataCorrected') {
    const correction = recordAt(data, 'correction');
    addGraphIssueUnless(
      recordAt(data, 'expense').id === correction.expenseId,
      context,
      ['data', 'expense', 'id'],
      'Cash expense correction does not identify its immutable expense.',
    );
    return;
  }

  if (eventType === 'cashExpenseReplaced') {
    const operation = recordAt(data, 'operation');
    const replacement = recordAt(data, 'replacement');
    const originalExpense = recordAt(data, 'originalExpense');
    const originalMovement = recordAt(data, 'originalMovement');
    const reversalMovement = recordAt(data, 'reversalMovement');
    const replacementMovement = recordAt(data, 'replacementMovement');
    addGraphIssueUnless(
      replacement.replacesExpenseId === originalExpense.id &&
        replacement.id !== originalExpense.id &&
        operation.scopeType === 'location' &&
        operation.scopeId === replacement.locationId &&
        replacement.status === 'active' &&
        replacement.operationId === operation.operationId &&
        replacement.requestHash === operation.requestHash &&
        replacement.createdByUserId === operation.actorUserId &&
        replacement.voidedByUserId === null &&
        replacement.voidReason === null &&
        replacement.voidedAt === null &&
        replacement.occurredAt === operation.occurredAt &&
        replacement.createdAt === operation.createdAt &&
        operation.createdAt === operation.occurredAt &&
        originalExpense.status === 'voided' &&
        typeof originalExpense.operationId === 'string' &&
        originalExpense.operationId !== operation.operationId &&
        typeof originalExpense.requestHash === 'string' &&
        originalExpense.voidedByUserId === operation.actorUserId &&
        typeof originalExpense.voidReason === 'string' &&
        originalExpense.voidReason.length > 0 &&
        originalExpense.voidedAt === operation.occurredAt &&
        (replacement.sourceType === 'drawer' || replacement.sourceType === 'vault') &&
        replacement.sourceType === originalExpense.sourceType &&
        recordsMatchFields(replacement, originalExpense, [
          'companyId',
          'storeId',
          'locationId',
          'stationId',
          'cashLocationId',
          'cashSessionId',
        ]) &&
        originalMovement.expenseId === originalExpense.id &&
        expenseMovementMatches(originalMovement, originalExpense, 'out', false, null) &&
        reversalMovement.expenseId === originalExpense.id &&
        expenseMovementMatches(
          reversalMovement,
          originalExpense,
          'in',
          true,
          originalMovement.id,
        ) &&
        reversalMovement.description === originalExpense.voidReason &&
        reversalMovement.occurredAt === originalExpense.voidedAt &&
        replacementMovement.expenseId === replacement.id &&
        expenseMovementMatches(replacementMovement, replacement, 'out', false, null) &&
        originalMovement.occurredAt === originalExpense.occurredAt &&
        replacementMovement.occurredAt === replacement.occurredAt &&
        originalMovement.description === originalExpense.description &&
        replacementMovement.description === replacement.description &&
        allDistinct([
          replacement.id,
          originalExpense.id,
          originalMovement.id,
          reversalMovement.id,
          replacementMovement.id,
        ]),
      context,
      ['data'],
      'Cash expense replacement result graph is semantically inconsistent.',
    );
    return;
  }

  if (eventType === 'cashDenominationsCorrected') {
    const operation = recordAt(data, 'operation');
    const correction = recordAt(data, 'correction');
    const movement = recordAt(data, 'movement');
    const allocation = recordAt(data, 'allocation');
    const priorRevision = recordAt(data, 'priorRevision');
    const correctedRevision = recordAt(data, 'correctedRevision');
    addGraphIssueUnless(
      correction.operationId === operation.operationId &&
        correction.requestHash === operation.requestHash &&
        correction.actorUserId === operation.actorUserId &&
        operation.scopeType === 'location' &&
        typeof movement.locationId === 'string' &&
        operation.scopeId === movement.locationId &&
        correction.occurredAt === operation.occurredAt &&
        correction.createdAt === operation.createdAt &&
        correction.createdAt === correction.occurredAt &&
        typeof correction.reason === 'string' &&
        correction.reason.length > 0 &&
        correction.reason === correction.reason.trim() &&
        correction.cashMovementId === movement.id &&
        correction.cashLocationId === movement.cashLocationId &&
        correction.cashSessionId === movement.cashSessionId &&
        correction.allocationId === allocation.id &&
        correction.priorAllocationRevisionId === priorRevision.id &&
        correction.correctedAllocationRevisionId === correctedRevision.id &&
        priorRevision.id !== correctedRevision.id &&
        allocation.cashMovementId === movement.id &&
        recordsMatchFields(allocation, movement, [
          'companyId',
          'storeId',
          'locationId',
          'stationId',
          'cashLocationId',
          'cashSessionId',
        ]) &&
        allocation.currentRevisionId === correctedRevision.id &&
        priorRevision.allocationId === allocation.id &&
        priorRevision.cashMovementId === movement.id &&
        correctedRevision.allocationId === allocation.id &&
        correctedRevision.cashMovementId === movement.id &&
        priorRevision.companyId === movement.companyId &&
        correctedRevision.companyId === movement.companyId &&
        correctedRevision.source === 'corrected' &&
        correctedRevision.confidence === 'corrected' &&
        correctedRevision.createdByUserId === operation.actorUserId &&
        correctedRevision.note === correction.reason &&
        correctedRevision.effectiveAt === movement.occurredAt &&
        instantMillis(correctedRevision.createdAt) > instantMillis(priorRevision.createdAt) &&
        denominationRevisionMatchesMovement(priorRevision, movement) &&
        denominationRevisionMatchesMovement(correctedRevision, movement) &&
        correctionRowsMatchRevision(correction.priorRowsJson, priorRevision) &&
        correctionRowsMatchRevision(correction.correctedRowsJson, correctedRevision) &&
        (correction.effect !== 'current_inventory' ||
          correction.countBoundaryCheckpointId === null) &&
        allDistinct([
          correction.id,
          movement.id,
          allocation.id,
          priorRevision.id,
          correctedRevision.id,
          ...revisionRowIds(priorRevision),
          ...revisionRowIds(correctedRevision),
        ]),
      context,
      ['data'],
      'Cash denomination correction result graph is semantically inconsistent.',
    );
    return;
  }

  if (eventType === 'cashTreasuryDistributionRequestDeclined') {
    addGraphIssueUnless(
      recordAt(data, 'instruction').status === 'declined',
      context,
      ['data', 'instruction', 'status'],
      'Cash Treasury distribution decline must contain a declined instruction.',
    );
    return;
  }

  if (eventType === 'cashCustodyReceiptRecorded') {
    const receipt = recordAt(data, 'receipt');
    addGraphIssueUnless(
      receipt.sourceType !== 'custody_receipt_reversal' &&
        receipt.vaultInMovementId === recordAt(data, 'vaultInMovement').id &&
        linkedNullableIdMatches(receipt.treasuryOutMovementId, data.treasuryOutMovement),
      context,
      ['data'],
      'Recorded Cash custody receipt graph identities do not match.',
    );
    return;
  }

  if (eventType === 'cashCustodyReceiptReconciled') {
    const receipt = recordAt(data, 'receipt');
    const reconciliation = recordAt(data, 'reconciliation');
    addGraphIssueUnless(
      receipt.sourceType !== 'custody_receipt_reversal' &&
        reconciliation.receiptId === receipt.id &&
        receipt.vaultInMovementId === recordAt(data, 'vaultInMovement').id &&
        linkedNullableIdMatches(receipt.treasuryOutMovementId, data.treasuryOutMovement),
      context,
      ['data'],
      'Reconciled Cash custody receipt graph identities do not match.',
    );
    return;
  }

  if (eventType === 'cashCustodyReceiptReversed') {
    const receipt = recordAt(data, 'receipt');
    addGraphIssueUnless(
      receipt.status === 'reversed' &&
        receipt.sourceType === 'custody_receipt_reversal' &&
        receipt.originalReceiptId === recordAt(data, 'originalReceipt').id &&
        receipt.vaultOutMovementId === recordAt(data, 'vaultOutMovement').id &&
        linkedNullableIdMatches(receipt.treasuryInMovementId, data.treasuryInMovement),
      context,
      ['data'],
      'Reversed Cash custody receipt graph identities do not match.',
    );
    return;
  }

  if (eventType.startsWith('cashVaultRemittance')) {
    const remittance = recordAt(data, 'remittance');
    const event = recordAt(data, 'event');
    const expectedEventType = {
      cashVaultRemittanceCreated: 'created',
      cashVaultRemittanceUpdated: 'updated',
      cashVaultRemittancePrepared: 'prepared',
      cashVaultRemittanceHandedOff: 'handed_off',
      cashVaultRemittanceConfirmed: 'confirmed',
      cashVaultRemittanceCancelled: 'cancelled',
      cashVaultRemittanceReturned: 'returned',
      cashVaultRemittanceExcepted: 'excepted',
      cashVaultRemittanceVoided: 'voided',
    }[eventType as Extract<NonVarianceTargetStateEventType, `cashVaultRemittance${string}`>];
    const reservationMatches =
      data.reservation === null || recordAt(data, 'reservation').remittanceId === remittance.id;
    addGraphIssueUnless(
      event.eventType === expectedEventType &&
        event.remittanceId === remittance.id &&
        reservationMatches &&
        linkedNullableIdMatches(remittance.vaultOutMovementId, data.vaultOutMovement) &&
        linkedNullableIdMatches(remittance.returnedVaultInMovementId, data.returnedVaultInMovement),
      context,
      ['data'],
      'Cash Vault Remittance event or graph identities do not match.',
    );
  }
}

function recordAt(value: Record<string, unknown>, key: string): Record<string, unknown> {
  return value[key] as Record<string, unknown>;
}

function recordsMatchFields(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
  fields: readonly string[],
): boolean {
  return fields.every((field) => canonicalJsonEqual(left[field], right[field]));
}

const forbiddenExpenseMovementRelationFields = [
  'transferId',
  'fundingSourceCategoryId',
  'fundingSourceCategoryCodeSnapshot',
  'fundingSourceCategoryDisplayNameSnapshot',
  'fundingSourceType',
  'fundingReference',
  'reasonCode',
  'varianceCaseId',
  'varianceActionId',
] as const;

function expenseMovementMatches(
  movement: Record<string, unknown>,
  expense: Record<string, unknown>,
  direction: 'in' | 'out',
  isReversal: boolean,
  reversalOfMovementId: unknown,
): boolean {
  return (
    movement.movementKind === 'expense' &&
    movement.direction === direction &&
    movement.amount === expense.amount &&
    movement.isReversal === isReversal &&
    movement.reversalOfMovementId === reversalOfMovementId &&
    movement.expenseId === expense.id &&
    movement.sourceType === 'expense' &&
    movement.sourceId === expense.id &&
    recordsMatchFields(movement, expense, [
      'companyId',
      'storeId',
      'locationId',
      'stationId',
      'cashLocationId',
      'cashSessionId',
    ]) &&
    forbiddenExpenseMovementRelationFields.every((field) => movement[field] === null)
  );
}

function allDistinct(values: readonly unknown[]): boolean {
  return (
    values.every((value) => typeof value === 'string') && new Set(values).size === values.length
  );
}

function instantMillis(value: unknown): number {
  return typeof value === 'string' ? Date.parse(value) : Number.NaN;
}

function revisionRows(revision: Record<string, unknown>): Record<string, unknown>[] {
  return Array.isArray(revision.rows) ? (revision.rows as Record<string, unknown>[]) : [];
}

function revisionRowIds(revision: Record<string, unknown>): unknown[] {
  return revisionRows(revision).map((row) => row.id);
}

function denominationRevisionMatchesMovement(
  revision: Record<string, unknown>,
  movement: Record<string, unknown>,
): boolean {
  const rows = revisionRows(revision);
  const denominations = new Set<number>();
  let totalMinor = 0;
  for (const row of rows) {
    const denominationMinor = row.denominationMinor;
    const count = row.count;
    const rowTotalMinor = row.rowTotalMinor;
    if (
      typeof denominationMinor !== 'number' ||
      typeof count !== 'number' ||
      typeof rowTotalMinor !== 'number' ||
      row.companyId !== revision.companyId ||
      row.revisionId !== revision.id ||
      rowTotalMinor !== denominationMinor * count ||
      denominations.has(denominationMinor)
    ) {
      return false;
    }
    denominations.add(denominationMinor);
    totalMinor += rowTotalMinor;
  }
  return (
    rows.length > 0 &&
    revision.denominationTotalMinor === movement.amount &&
    totalMinor === revision.denominationTotalMinor
  );
}

function correctionRowsMatchRevision(
  correctionRows: unknown,
  revision: Record<string, unknown>,
): boolean {
  if (!Array.isArray(correctionRows)) {
    return false;
  }
  const rows = revisionRows(revision);
  if (correctionRows.length !== rows.length) {
    return false;
  }
  const revisionCountByDenomination = new Map(
    rows.map((row) => [row.denominationMinor, row.count] as const),
  );
  return correctionRows.every((row) => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) {
      return false;
    }
    const record = row as Record<string, unknown>;
    return revisionCountByDenomination.get(record.denom) === record.count;
  });
}

function linkedNullableIdMatches(expectedId: unknown, node: unknown): boolean {
  return expectedId === null
    ? node === null
    : Boolean(
        node && typeof node === 'object' && (node as Record<string, unknown>).id === expectedId,
      );
}

function addGraphIssueUnless(
  condition: boolean,
  context: z.RefinementCtx,
  path: (string | number)[],
  message: string,
): void {
  if (!condition) {
    context.addIssue({ code: 'custom', message, path });
  }
}

function validateGraphCompanyIds(
  value: unknown,
  expectedCompanyId: string,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      validateGraphCompanyIds(entry, expectedCompanyId, context, [...path, index]);
    });
    return;
  }
  if (!value || typeof value !== 'object') {
    return;
  }
  const record = value as Record<string, unknown>;
  if ('companyId' in record && record.companyId !== expectedCompanyId) {
    context.addIssue({
      code: 'custom',
      message: 'Target-state result graph cannot cross company boundaries.',
      path: [...path, 'companyId'],
    });
  }
  for (const [key, entry] of Object.entries(record)) {
    validateGraphCompanyIds(entry, expectedCompanyId, context, [...path, key]);
  }
}

const expectedVarianceGraphCounts: Record<
  CashVarianceOperationSyncEventType,
  readonly [cases: number, actions: number, movements: number]
> = {
  cashVarianceRecognized: [1, 0, 1],
  cashVarianceRecoverySubmitted: [1, 1, 0],
  cashVarianceRecoveryApproved: [1, 2, 1],
  cashVarianceRecoveryRejected: [1, 2, 0],
  cashVarianceWriteoffApplied: [1, 1, 0],
  cashVarianceOffsetApplied: [2, 2, 0],
  cashVarianceOverageClassified: [1, 1, 0],
  cashVarianceOverageRemoved: [1, 1, 1],
};

const expectedVarianceResultEntityTypes: Record<
  CashVarianceOperationSyncEventType,
  readonly string[]
> = {
  cashVarianceRecognized: ['cashSessionOpen', 'cashSessionClose', 'cashSessionForceClose'],
  cashVarianceRecoverySubmitted: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoverySubmitted],
  cashVarianceRecoveryApproved: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoveryApproved],
  cashVarianceRecoveryRejected: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.recoveryRejected],
  cashVarianceWriteoffApplied: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.writeoffApplied],
  cashVarianceOffsetApplied: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.offsetApplied],
  cashVarianceOverageClassified: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.overageClassified],
  cashVarianceOverageRemoved: [CASH_VARIANCE_OPERATION_ENTITY_TYPES.overageRemoved],
};

const requiredVarianceGraphKeys = {
  cases: [
    'id',
    'companyId',
    'cashLocationId',
    'cashSessionId',
    'stage',
    'initialAmountMinor',
    'outstandingAmountMinor',
    'recoveredAmountMinor',
    'writtenOffAmountMinor',
    'status',
    'openedByUserId',
    'openedAt',
    'resolvedAt',
    'createdAt',
    'updatedAt',
  ],
  actions: [
    'id',
    'companyId',
    'caseId',
    'actionType',
    'status',
    'amountMinor',
    'recoveryDirection',
    'denominations',
    'note',
    'submissionActionId',
    'targetCashLocationId',
    'targetCashSessionId',
    'linkedMovementId',
    'relatedCaseId',
    'relatedActionId',
    'classification',
    'submittedByUserId',
    'reviewedByUserId',
    'submittedAt',
    'reviewedAt',
    'createdAt',
    'updatedAt',
  ],
  movements: [
    'id',
    'companyId',
    'storeId',
    'locationId',
    'stationId',
    'cashLocationId',
    'cashSessionId',
    'transferId',
    'expenseId',
    'movementKind',
    'direction',
    'amount',
    'description',
    'isReversal',
    'reversalOfMovementId',
    'sourceType',
    'sourceId',
    'fundingSourceCategoryId',
    'fundingSourceCategoryCodeSnapshot',
    'fundingSourceCategoryDisplayNameSnapshot',
    'fundingSourceType',
    'fundingReference',
    'reasonCode',
    'varianceCaseId',
    'varianceActionId',
    'occurredAt',
    'createdAt',
    'updatedAt',
  ],
} as const satisfies Record<'cases' | 'actions' | 'movements', readonly string[]>;

function validateCashVarianceOperationPayload(
  value: TargetStatePayloadForValidation,
  context: z.RefinementCtx,
): void {
  if (!isCashVarianceOperationEventType(value.eventType)) {
    return;
  }

  const parsedData = CashVarianceOperationSyncDataDtoSchema.safeParse(value.data);
  if (!parsedData.success) {
    for (const issue of parsedData.error.issues) {
      context.addIssue({
        code: 'custom',
        message: issue.message,
        path: ['data', ...issue.path],
      });
    }
    return;
  }

  const data = parsedData.data;
  if (!canonicalJsonEqual(value.data.operation, data.operation)) {
    context.addIssue({
      code: 'custom',
      message: 'Cash variance operation envelope must already be in canonical form.',
      path: ['data', 'operation'],
    });
  }
  for (const collection of ['cases', 'actions', 'movements'] as const) {
    const rawItems = (value.data[collection] ?? []) as unknown[];
    data[collection].forEach((item, index) => {
      if (!canonicalJsonEqual(rawItems[index], item)) {
        context.addIssue({
          code: 'custom',
          message: 'Cash variance operation bundle records cannot contain unsupported fields.',
          path: ['data', collection, index],
        });
      }
      const rawItem = rawItems[index];
      if (!rawItem || typeof rawItem !== 'object' || Array.isArray(rawItem)) {
        return;
      }
      const rawRecord = rawItem as Record<string, unknown>;
      for (const key of requiredVarianceGraphKeys[collection]) {
        if (!Object.prototype.hasOwnProperty.call(rawRecord, key) || rawRecord[key] === undefined) {
          context.addIssue({
            code: 'custom',
            message: 'Cash variance operation bundle records must contain every canonical field.',
            path: ['data', collection, index, key],
          });
        }
      }
    });
  }
  const operation = data.operation;
  const expectedResultTypes = expectedVarianceResultEntityTypes[value.eventType];
  if (!expectedResultTypes.includes(value.resultEntityType)) {
    context.addIssue({
      code: 'custom',
      message: 'Cash variance event type does not match its operation result entity type.',
      path: ['resultEntityType'],
    });
  }

  if (
    operation.operationId !== value.operationId ||
    operation.requestHash !== value.requestHash ||
    operation.companyId !== value.companyId ||
    operation.scopeType !== value.scopeType ||
    operation.scopeId !== value.scopeId ||
    operation.actorUserId !== value.actorUserId ||
    operation.occurredAt !== value.occurredAt ||
    operation.createdAt !== value.occurredAt ||
    operation.resultEntityType !== value.resultEntityType
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Cash variance operation envelope must equal the top-level mutation identity.',
      path: ['data', 'operation'],
    });
  }

  if (value.id !== operation.resultEntityId) {
    context.addIssue({
      code: 'custom',
      message: 'Cash variance lifecycle identity must equal the operation result entity identity.',
      path: ['id'],
    });
  }

  const [expectedCases, expectedActions, expectedMovements] =
    expectedVarianceGraphCounts[value.eventType];
  if (
    data.cases.length !== expectedCases ||
    data.actions.length !== expectedActions ||
    data.movements.length !== expectedMovements
  ) {
    context.addIssue({
      code: 'custom',
      message: 'Cash variance operation bundle has an invalid result graph shape.',
      path: ['data'],
    });
  }

  const graphNodes = [
    ...data.cases.map((item, index) => ({ id: item.id, path: ['cases', index, 'id'] })),
    ...data.actions.map((item, index) => ({ id: item.id, path: ['actions', index, 'id'] })),
    ...data.movements.map((item, index) => ({ id: item.id, path: ['movements', index, 'id'] })),
  ];
  const seenGraphIds = new Set<string>();
  for (const node of graphNodes) {
    if (seenGraphIds.has(node.id)) {
      context.addIssue({
        code: 'custom',
        message: 'Cash variance operation bundle graph IDs must be unique.',
        path: ['data', ...node.path],
      });
    }
    seenGraphIds.add(node.id);
  }

  for (const [collection, items] of [
    ['cases', data.cases],
    ['actions', data.actions],
    ['movements', data.movements],
  ] as const) {
    items.forEach((item, index) => {
      if (item.companyId !== value.companyId) {
        context.addIssue({
          code: 'custom',
          message: 'Cash variance operation bundle cannot cross company boundaries.',
          path: ['data', collection, index, 'companyId'],
        });
      }
    });
  }
}

function canonicalJsonEqual(left: unknown, right: unknown): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((entry, index) => canonicalJsonEqual(entry, right[index]))
    );
  }
  if (left && right && typeof left === 'object' && typeof right === 'object') {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord).sort();
    const rightKeys = Object.keys(rightRecord).sort();
    return (
      leftKeys.length === rightKeys.length &&
      leftKeys.every(
        (key, index) =>
          key === rightKeys[index] && canonicalJsonEqual(leftRecord[key], rightRecord[key]),
      )
    );
  }
  return Object.is(left, right);
}

function isCashVarianceOperationEventType(
  eventType: CashTargetStateSyncEventType,
): eventType is CashVarianceOperationSyncEventType {
  return (cashVarianceOperationSyncEventTypeValues as readonly string[]).includes(eventType);
}
